/**
 * CR306: Offer-safe section — block legal/terms pages from primary offer extraction
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Save } from "lucide-react";
import type { Recipe } from "@/lib/recipes-studio/types";
import type { FirecrawlRecipeConfig } from "@/lib/recipes-studio/config-types";
import { DEFAULT_OFFER_SAFE_PATTERNS } from "@/lib/recipes-studio/config-types";

interface RecipeOfferSafeSectionProps {
  recipe: Recipe;
  saving: boolean;
  onSave: (updates: Partial<Recipe>) => void;
}

export function RecipeOfferSafeSection({ recipe, saving, onSave }: RecipeOfferSafeSectionProps) {
  const cfg = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
  const offerSafe = cfg.offer_safe ?? {};

  const [enabled, setEnabled] = useState(offerSafe.enabled ?? true);
  const [patterns, setPatterns] = useState(
    (offerSafe.block_patterns ?? DEFAULT_OFFER_SAFE_PATTERNS).join("\n")
  );

  useEffect(() => {
    const c = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
    const os = c.offer_safe ?? {};
    setEnabled(os.enabled ?? true);
    setPatterns((os.block_patterns ?? DEFAULT_OFFER_SAFE_PATTERNS).join("\n"));
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentPatterns = (offerSafe.block_patterns ?? DEFAULT_OFFER_SAFE_PATTERNS).join("\n");
  const hasChanges =
    enabled !== (offerSafe.enabled ?? true) ||
    patterns !== currentPatterns;

  const handleSave = useCallback(() => {
    const patternList = patterns
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const updated: FirecrawlRecipeConfig = {
      ...cfg,
      offer_safe: { enabled, block_patterns: patternList },
    };
    onSave({ recipe_config: updated as Record<string, unknown> });
  }, [cfg, enabled, patterns, onSave]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" />
            Offer-safe
          </CardTitle>
          <Badge variant={enabled ? "default" : "outline"} className="text-[10px]">
            {enabled ? "Active" : "Off"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs">Block Legal Pages</Label>
            <p className="text-[11px] text-muted-foreground">
              Prevent /legal/*, /terms/*, etc. from becoming primary offer sources
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled && (
          <div className="space-y-1.5">
            <Label className="text-xs">Block Patterns (one per line, glob syntax)</Label>
            <Textarea
              value={patterns}
              onChange={(e) => setPatterns(e.target.value)}
              placeholder="/legal/*&#10;/terms/*&#10;/privacy/*"
              className="font-mono text-xs min-h-[100px]"
            />
            <p className="text-[11px] text-muted-foreground">
              URLs matching these patterns are treated as fine-print only, never primary offers.
            </p>
          </div>
        )}

        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
